/**
 * Empty live session: waiting for match (GSI auto-create or strategist Start Match).
 * Route: /live/:teamId/sessions/:sessionId
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useIsStrategist } from '../hooks/useIsStrategist';
import { useOpenLiveSession } from '../hooks/useOpenLiveSession';
import { createMatch, joinLiveSession } from '../liveSessionApi';
import { buildLivePlayerPath, liveMatchOnSession } from '../lib/pickJoinTarget';
import styles from './LiveSessionWaitingPage.module.css';

const COMMON_MAPS = [
  'de_mirage',
  'de_inferno',
  'de_dust2',
  'de_nuke',
  'de_ancient',
  'de_anubis',
  'de_vertigo',
];

export default function LiveSessionWaitingPage() {
  const { teamId, sessionId } = useParams<{ teamId: string; sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStrategist = useIsStrategist(teamId);
  const { sessions, refetch } = useOpenLiveSession(user?.id, teamId, 3_000);

  const [joinError, setJoinError] = useState<string | null>(null);
  const [map, setMap] = useState('de_mirage');
  const [startingSide, setStartingSide] = useState<'' | 'T' | 'CT'>('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  // Join roster once
  useEffect(() => {
    if (!teamId || !sessionId) return;
    let cancelled = false;
    joinLiveSession(teamId, sessionId)
      .then(() => {
        if (!cancelled) setJoinError(null);
      })
      .catch((err) => {
        if (!cancelled) setJoinError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [teamId, sessionId]);

  // Auto-navigate when a live match appears (e.g. GSI created it)
  useEffect(() => {
    if (!teamId || !sessionId) return;
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) return;
    const live = liveMatchOnSession(session);
    if (!live) return;
    navigate(
      buildLivePlayerPath({
        teamId,
        sessionId,
        matchId: live.id,
        map: live.map,
      }),
      { replace: true },
    );
  }, [sessions, teamId, sessionId, navigate]);

  const handleStartMatch = useCallback(async () => {
    if (!teamId || !sessionId || starting) return;
    const mapName = map.trim();
    if (!mapName) {
      setStartError('Map is required');
      return;
    }
    setStarting(true);
    setStartError(null);
    try {
      const match = await createMatch(teamId, sessionId, {
        map: mapName,
        ...(startingSide ? { startingSide } : {}),
      });
      refetch();
      navigate(
        buildLivePlayerPath({
          teamId,
          sessionId,
          matchId: match.id,
          map: match.map || mapName,
        }),
        { replace: true },
      );
    } catch (err) {
      setStartError(err instanceof Error ? err.message : String(err));
    } finally {
      setStarting(false);
    }
  }, [teamId, sessionId, map, startingSide, starting, navigate, refetch]);

  if (!teamId || !sessionId) {
    return (
      <div className={styles.shell}>
        <p className={styles.body}>Invalid session link.</p>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <div className={styles.card}>
        <div className={styles.statusRow}>
          <span className={styles.statusDot} aria-hidden="true" />
          <span className={styles.statusLabel}>Standby</span>
        </div>
        <h1 className={styles.title}>Waiting for match to start</h1>
        <p className={styles.body}>
          This live session is open. A match will appear when GSI starts reporting, or when the strategist
          starts a match manually.
        </p>
        {joinError && (
          <p className={styles.error} role="alert">
            Could not join session roster: {joinError}
          </p>
        )}

        {isStrategist && (
          <div className={styles.startBlock}>
            <h2 className={styles.subhead}>Start Match</h2>
            <label className={styles.field}>
              <span>Map</span>
              <select value={map} onChange={(e) => setMap(e.target.value)}>
                {COMMON_MAPS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Starting side (optional)</span>
              <select
                value={startingSide}
                onChange={(e) => setStartingSide(e.target.value as '' | 'T' | 'CT')}
              >
                <option value="">Unset</option>
                <option value="T">T</option>
                <option value="CT">CT</option>
              </select>
            </label>
            {startError && (
              <p className={styles.error} role="alert">
                {startError}
              </p>
            )}
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => void handleStartMatch()}
              disabled={starting}
            >
              {starting ? 'Starting…' : 'Start Match'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
